import { ReactNode } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Modal from "components/DesignSystem/Modal/Modal";
import { FileInput } from "components/FileInput/FileInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { DISPLAY_DATE } from "constants/date";
import { useToast } from "hooks/useToast";
import { useUploadQBOfileMutation } from "store/apis/qbo";
import { BackendError } from "types/utils/error";
import { QBOAddDetailsValidation } from "formValidations/QBOAddDetailsValidation";
import { HowItWorksModal } from "./HowItWorksModal";
import { ImportQBOHeader, useImportQBO } from "./ImportQBO";
import { QBOWhereToFindAccordionContent } from "./QBOImportReview";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Info } from "components/icons/Info";
import {
  QBO_ACCOUNT_LIST_CSV_URL,
  QBO_BALANCE_SHEET_CSV_URL,
  QBO_BILLS_AND_APPLIED_PAYMENTS_CSV_URL,
  QBO_CUSTOMERS_CSV_URL,
  QBO_INVOICES_AND_RECEIVED_PAYMENTS_CSV_URL,
  QBO_JOURNAL_CSV_URL,
  QBO_VENDORS_CSV_URL,
} from "types/contants/sampleExcelSheet";
import { ConditionalLink } from "components/conditionalLink";

export type AddDetailsFormValues = {
  date_till_import: string;
  prev_fy_end_date: string;
  coa_file: File | null;
  balance_sheet_file: File | null;
  vendor_file: File | null;
  customer_file: File | null;
  je_file: File | null;
  invoice_file: File | null;
  bill_file: File | null;
};

type AddDetailsStepProps = {
  onNext: () => void;
  entityId: string;
  setImportProcessId: (id: string) => void;
};

type QBOFileFieldName = Exclude<
  keyof AddDetailsFormValues,
  "date_till_import" | "prev_fy_end_date"
>;

type QBOFileFieldConfig = {
  name: QBOFileFieldName;
  label: ReactNode;
  sampleFileUrl: string;
};

const QBO_FILE_FIELDS: QBOFileFieldConfig[] = [
  {
    name: "coa_file",
    label: "Chart of Accounts",
    sampleFileUrl: QBO_ACCOUNT_LIST_CSV_URL,
  },
  {
    name: "balance_sheet_file",
    label: (
      <>
        Previous financial year's Balance sheet
        <ConditionalToolTip
          condition={
            <div className="t-text-center t-w-60">
              Upload the balance sheet as of your last financial year-end. This
              helps us set accurate opening balances for your chart of accounts.
            </div>
          }
          side="right"
        >
          <span className="t-ml-1">
            <Info />
          </span>
        </ConditionalToolTip>
      </>
    ),
    sampleFileUrl: QBO_BALANCE_SHEET_CSV_URL,
  },
  {
    name: "vendor_file",
    label: "Vendors List",
    sampleFileUrl: QBO_VENDORS_CSV_URL,
  },
  {
    name: "customer_file",
    label: "Customers List",
    sampleFileUrl: QBO_CUSTOMERS_CSV_URL,
  },
  {
    name: "je_file",
    label: "Journal Entries",
    sampleFileUrl: QBO_JOURNAL_CSV_URL,
  },
  {
    name: "invoice_file",
    label: "Invoices and Received Payments report",
    sampleFileUrl: QBO_INVOICES_AND_RECEIVED_PAYMENTS_CSV_URL,
  },
  {
    name: "bill_file",
    label: "Bills and Applied Payments report",
    sampleFileUrl: QBO_BILLS_AND_APPLIED_PAYMENTS_CSV_URL,
  },
];

type QBOFileInputFieldProps = {
  name: QBOFileFieldName;
  label: ReactNode;
  file: File | null;
  sampleFileUrl: string;
};

const QBOFileInputField = ({
  name,
  label,
  file,
  sampleFileUrl,
}: QBOFileInputFieldProps) => {
  return (
    <FileInput
      label={label}
      description={
        <span className="t-ml-1.5">
          Click <ConditionalLink to={sampleFileUrl}>here</ConditionalLink> to
          download sample file
        </span>
      }
      withForm
      name={name}
      accept={{
        "text/csv": [".csv"],
      }}
      file={
        file
          ? {
              name: file.name,
              file_type: "CSV",
              uuid: "",
              is_previewable: false,
            }
          : null
      }
      required
    />
  );
};

export const AddDetailsStep = ({
  onNext,
  entityId,
  setImportProcessId,
}: AddDetailsStepProps) => {
  const { alertToast } = useToast();
  const { addDetailsFormValues, setAddDetailsFormValues, isOpen, close } =
    useImportQBO();
  const [uploadQBOfile] = useUploadQBOfileMutation();

  const handleSubmit = async (values: AddDetailsFormValues) => {
    try {
      const response = await uploadQBOfile({
        payload: {
          date_till_import: values.date_till_import,
          prev_fy_end_date: values.prev_fy_end_date,
          coa_file: values.coa_file as File,
          balance_sheet_file: values.balance_sheet_file as File,
          vendor_file: values.vendor_file as File,
          customer_file: values.customer_file as File,
          je_file: values.je_file as File,
          invoice_file: values.invoice_file as File,
          bill_file: values.bill_file as File,
        },
        entityId,
      }).unwrap();

      setAddDetailsFormValues(values);
      setImportProcessId(response.coa_import_state.coa_import_state_id);
      onNext();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const defaultInitialValues: AddDetailsFormValues = {
    date_till_import: "",
    prev_fy_end_date: "",
    coa_file: null,
    balance_sheet_file: null,
    vendor_file: null,
    customer_file: null,
    je_file: null,
    invoice_file: null,
    bill_file: null,
  };

  const initialValues = addDetailsFormValues || defaultInitialValues;

  return (
    <FormikForm<AddDetailsFormValues>
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={QBOAddDetailsValidation}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, submitForm, isSubmitting, isValid, dirty }) => {
        const hasExistingData = Boolean(addDetailsFormValues);
        const shouldTakeConfirmation = dirty || hasExistingData;

        return (
          <Modal.Root
            open={isOpen}
            onOpenChange={() =>
              close({ takeConfirmation: shouldTakeConfirmation })
            }
            modal={false}
          >
            <Modal.Content size="xl" useCustomOverlay>
              <ImportQBOHeader />
              <Modal.Body className="t-flex t-flex-col t-gap-5 sm:t-px-4">
                <DateField
                  name="date_till_import"
                  dateProps={{
                    label:
                      "Date till which data needs to be imported from Quickbooks",
                    required: true,
                    placeholder: DISPLAY_DATE,
                  }}
                />
                <DateField
                  name="prev_fy_end_date"
                  dateProps={{
                    tooltipText: (
                      <div className="t-text-center t-w-60">
                        Select the closing date of your last financial year. We
                        use this date to determine the opening balances.
                      </div>
                    ),
                    label: "Previous financial year's end date",
                    required: true,
                    placeholder: DISPLAY_DATE,
                  }}
                />
                <div className="t-text-md t-font-medium">
                  Documents
                  <QBOWhereToFindAccordionContent />
                </div>
                {QBO_FILE_FIELDS.map((field) => (
                  <QBOFileInputField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    file={values[field.name]}
                    sampleFileUrl={field.sampleFileUrl}
                  />
                ))}
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <HowItWorksModal />
                <Modal.RawClose asChild>
                  <Button type="reset" disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  onClick={submitForm}
                  disabled={isSubmitting || !isValid}
                  isLoading={isSubmitting}
                >
                  Next
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          </Modal.Root>
        );
      }}
    </FormikForm>
  );
};
